import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import {
  Card, Button, Modal, Form, Badge, Alert, Spinner, ListGroup, Row, Col
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useGoogleMeet } from '../auth/GoogleMeetProvider';
import { db } from '../firebase';
import { canManageSchedules, isExternalRole, normalizeRole } from '../services/roleManagementService';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  query, where, orderBy, serverTimestamp, Timestamp
} from 'firebase/firestore';

const MEETINGS_COLLECTION = 'meetings';

const GoogleMeetLinks = () => {
  const { currentUser, userProfile } = useAuth();
  const { isGoogleAuthenticated, googleMeetService, signInWithGoogle } = useGoogleMeet();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [creatingMeeting, setCreatingMeeting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meetLink: '',
    scheduledDate: '',
    scheduledTime: '',
    duration: '60',
    programId: '',
    cohortId: '',
    cohortIds: [],
    assignedUserIds: [],
    assignedUserEmails: '',
    sendReminder: true,
    reminderMinutes: 30,
    isRecurring: false,
    recurrenceEndDate: '',
    recurrenceDays: [1, 2, 3, 4, 5],
  });

  const [programs, setPrograms] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [externalUsers, setExternalUsers] = useState([]);

  const role = normalizeRole(userProfile?.role);
  const isFacilitatorOrAdmin = ['facilitator', 'admin'].includes(role);
  const isFacilitator = role === 'facilitator';
  const isExternal = isExternalRole(role) || role === 'external';
  const isQA = role === 'qa';
  const allowScheduleEdits = canManageSchedules(role);

  useEffect(() => {
    if (currentUser) {
      loadMeetings();
      if (isFacilitatorOrAdmin || isExternal || isQA) {
        loadPrograms();
        loadCohorts();
      } else if (userProfile?.role === 'learner') {
        loadPrograms();
      }
      if (isFacilitatorOrAdmin) loadAssignableUsers();
    }
  }, [currentUser, userProfile?.role, userProfile?.cohortId, userProfile?.programId, cohorts.length]);

  const loadAssignableUsers = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, 'users'),
        where('role', 'in', [
          'facilitator', 'external', 'external_facilitator',
          'external_facilitator_limited', 'external_viewer', 'external_viewer_reporting'
        ])
      ));
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFacilitators(users.filter(u => normalizeRole(u.role) === 'facilitator'));
      setExternalUsers(users.filter(u => normalizeRole(u.role) !== 'facilitator'));
    } catch (err) {
      devError('Error loading assignable users:', err);
    }
  };

  const loadPrograms = async () => {
    try {
      const programsRef = collection(db, 'programs');
      if (isExternal) {
        const assigned = Array.isArray(userProfile?.assignedPrograms) ? userProfile.assignedPrograms : [];
        if (!assigned.length) { setPrograms([]); return; }
        const docs = [];
        for (let i = 0; i < assigned.length; i += 10) {
          const snap = await getDocs(query(programsRef, where('__name__', 'in', assigned.slice(i, i + 10))));
          docs.push(...snap.docs);
        }
        setPrograms(docs.map(d => ({ id: d.id, ...d.data() })));
        return;
      }
      const snap = await getDocs(programsRef);
      setPrograms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { devError('Error loading programs:', err); }
  };

  const loadCohorts = async () => {
    try {
      const cohortsRef = collection(db, 'cohorts');
      if (isExternal) {
        const assignedCohorts = Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [];
        const assignedPrograms = Array.isArray(userProfile?.assignedPrograms) ? userProfile.assignedPrograms : [];
        const docs = [];
        if (assignedCohorts.length) {
          for (let i = 0; i < assignedCohorts.length; i += 10) {
            const snap = await getDocs(query(cohortsRef, where('__name__', 'in', assignedCohorts.slice(i, i + 10))));
            docs.push(...snap.docs);
          }
        } else if (assignedPrograms.length) {
          for (let i = 0; i < assignedPrograms.length; i += 10) {
            const snap = await getDocs(query(cohortsRef, where('programId', 'in', assignedPrograms.slice(i, i + 10))));
            docs.push(...snap.docs);
          }
        }
        setCohorts(docs.map(d => ({ id: d.id, ...d.data() })));
        return;
      }
      const snap = await getDocs(cohortsRef);
      setCohorts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { devError('Error loading cohorts:', err); }
  };

  const parseMeetingDate = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getRecurrenceDays = (meeting) => {
    const days = Array.isArray(meeting.recurrenceDays) ? meeting.recurrenceDays : [];
    return days.length > 0 ? days : [1, 2, 3, 4, 5];
  };

  const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  };

  const buildOccurrences = (meeting, rangeStart, rangeEnd) => {
    const occurrences = [];
    if (!meeting.scheduledDateTime) return occurrences;
    const recurrenceEnd = meeting.recurrenceEndDate || null;
    const windowEnd = recurrenceEnd && recurrenceEnd < rangeEnd ? recurrenceEnd : rangeEnd;
    const current = new Date(meeting.scheduledDateTime);
    current.setSeconds(0, 0);
    if (current < rangeStart) {
      const daysDiff = Math.ceil((rangeStart - current) / (24 * 60 * 60 * 1000));
      current.setDate(current.getDate() + daysDiff);
    }
    const recurrenceDays = getRecurrenceDays(meeting);
    while (current <= windowEnd) {
      if (recurrenceDays.includes(current.getDay())) {
        occurrences.push({
          ...meeting,
          id: `${meeting.id}_${current.toISOString().split('T')[0]}`,
          scheduledDateTime: new Date(current),
          baseMeetingId: meeting.id,
          baseMeeting: meeting,
        });
      }
      current.setDate(current.getDate() + 1);
    }
    return occurrences;
  };

  const getNextRecurringDate = (meeting, fromDateTime) => {
    if (!meeting?.scheduledDateTime) return null;
    const recurrenceDays = getRecurrenceDays(meeting);
    if (!recurrenceDays.length) return null;
    const recurrenceEnd = meeting.recurrenceEndDate || null;
    const baseTime = new Date(meeting.scheduledDateTime);
    const cursor = new Date(fromDateTime);
    cursor.setSeconds(0, 0);
    for (let offset = 0; offset <= 370; offset++) {
      const candidate = addDays(cursor, offset);
      candidate.setHours(baseTime.getHours(), baseTime.getMinutes(), 0, 0);
      if (!recurrenceDays.includes(candidate.getDay())) continue;
      if (candidate <= fromDateTime) continue;
      if (recurrenceEnd && candidate > recurrenceEnd) return null;
      return candidate;
    }
    return null;
  };

  const loadMeetings = async () => {
    try {
      setLoading(true);
      setError('');

      const nowDateTime = new Date();
      const now = new Date(nowDateTime);
      now.setHours(0, 0, 0, 0);

      const meetingsRef = collection(db, MEETINGS_COLLECTION);
      const upcomingSnap = await getDocs(query(
        meetingsRef,
        where('scheduledDateTime', '>=', Timestamp.fromDate(now)),
        orderBy('scheduledDateTime', 'asc')
      ));
      const recurringSnap = await getDocs(query(meetingsRef, where('isRecurring', '==', true)));

      const mergedDocs = new Map();
      upcomingSnap.docs.forEach(d => mergedDocs.set(d.id, d));
      recurringSnap.docs.forEach(d => mergedDocs.set(d.id, d));

      const allMeetings = Array.from(mergedDocs.values()).map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          scheduledDateTime: parseMeetingDate(data.scheduledDateTime),
          recurrenceEndDate: parseMeetingDate(data.recurrenceEndDate),
        };
      });

      if (isFacilitatorOrAdmin) {
        const getDurationMinutes = (m) => {
          const p = Number(m?.duration);
          return Number.isFinite(p) && p > 0 ? p : 60;
        };
        for (const meeting of allMeetings) {
          if (!meeting?.isRecurring || !meeting?.scheduledDateTime) continue;
          const canRoll =
            userProfile?.role === 'admin' ||
            (isFacilitator && !isExternal && meeting.createdById === currentUser?.uid);
          if (!canRoll) continue;
          const meetingEnd = new Date(
            meeting.scheduledDateTime.getTime() + getDurationMinutes(meeting) * 60 * 1000
          );
          if (meetingEnd > nowDateTime) continue;
          const nextDate = getNextRecurringDate(meeting, nowDateTime);
          if (!nextDate) continue;
          try {
            await updateDoc(doc(db, MEETINGS_COLLECTION, meeting.id), {
              scheduledDateTime: Timestamp.fromDate(nextDate),
              updatedAt: serverTimestamp(),
              autoRolledForwardAt: serverTimestamp(),
            });
            meeting.scheduledDateTime = nextDate;
          } catch (rollErr) {
            devError('Failed to roll recurring meeting forward', rollErr);
          }
        }
      }

      const rangeEnd = addDays(now, 30);
      const allUpcoming = allMeetings.flatMap(m =>
        m.isRecurring ? buildOccurrences(m, now, rangeEnd) : [m]
      ).filter(m => m.scheduledDateTime && m.scheduledDateTime >= now);

      let filtered = [];
      if (userProfile?.role === 'learner') {
        const learnerCohortId = userProfile?.cohortId;
        filtered = allUpcoming.filter(m => {
          const emails = (m.assignedUserEmails || []).map(e => e.toLowerCase());
          if (currentUser?.email && emails.includes(currentUser.email.toLowerCase())) return true;
          const cohortIds = Array.isArray(m.cohortIds) ? m.cohortIds : (m.cohortId ? [m.cohortId] : []);
          return learnerCohortId && cohortIds.includes(learnerCohortId);
        });
      } else if (isFacilitator || isExternal) {
        const assignedProgramIds = userProfile?.assignedPrograms || [];
        const assignedCohortIds = userProfile?.assignedCohorts || [];
        filtered = allUpcoming.filter(m => {
          if ((m.assignedUserIds || []).includes(currentUser?.uid)) return true;
          const emails = (m.assignedUserEmails || []).map(e => e.toLowerCase());
          if (currentUser?.email && emails.includes(currentUser.email.toLowerCase())) return true;
          const cohortIds = Array.isArray(m.cohortIds) ? m.cohortIds : (m.cohortId ? [m.cohortId] : []);
          const cohortMatch = cohortIds.some(id => assignedCohortIds.includes(id));
          if (isExternal) {
            return cohortMatch || (m.programId && assignedProgramIds.includes(m.programId)) || (!m.programId && !m.cohortId);
          }
          return (!m.programId && !m.cohortId) || (m.programId && assignedProgramIds.includes(m.programId)) || cohortMatch;
        });
      } else {
        filtered = allUpcoming;
      }

      setMeetings([...filtered].sort((a, b) => new Date(a.scheduledDateTime) - new Date(b.scheduledDateTime)));
    } catch (err) {
      devError('Error loading meetings:', err);
      setError('Failed to load meetings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWithGoogle = async () => {
    if (!formData.title.trim() || !formData.scheduledDate || !formData.scheduledTime) {
      setError('Please fill in title, date and time before creating via Google Calendar.');
      return;
    }
    setCreatingMeeting(true);
    const startDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
    const endDateTime = new Date(startDateTime.getTime() + parseInt(formData.duration) * 60000);
    const result = await googleMeetService.createMeeting({
      title: formData.title,
      description: formData.description,
      startDateTime,
      endDateTime,
    });
    if (result.success) {
      setFormData(prev => ({ ...prev, meetLink: result.data.meetLink }));
      setSuccess('Meet link created! Review details and click Save.');
    } else {
      setError('Failed to create meeting: ' + result.error);
    }
    setCreatingMeeting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { setError('Please enter a meeting title.'); return; }
    if (!formData.meetLink.trim()) { setError('Please enter or create a Meet link.'); return; }
    if (!formData.scheduledDate || !formData.scheduledTime) { setError('Please select date and time.'); return; }
    if (formData.isRecurring && (!formData.recurrenceDays || !formData.recurrenceDays.length)) {
      setError('Please select at least one recurring day.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      const recurrenceEndDate = formData.recurrenceEndDate
        ? Timestamp.fromDate(new Date(`${formData.recurrenceEndDate}T23:59:59`))
        : null;
      const parseAssignedEmails = (raw) =>
        (raw || '').split(/[\n,;]/).map(e => e.trim().toLowerCase()).filter(Boolean);

      const meetingData = {
        title: formData.title,
        description: formData.description,
        meetLink: formData.meetLink,
        scheduledDateTime: Timestamp.fromDate(scheduledDateTime),
        duration: parseInt(formData.duration),
        programId: formData.programId || null,
        cohortId: formData.cohortId || null,
        cohortIds: Array.isArray(formData.cohortIds) && formData.cohortIds.length
          ? formData.cohortIds
          : (formData.cohortId ? [formData.cohortId] : []),
        assignedUserIds: formData.assignedUserIds || [],
        assignedUserEmails: parseAssignedEmails(formData.assignedUserEmails),
        sendReminder: formData.sendReminder,
        reminderMinutes: parseInt(formData.reminderMinutes),
        isRecurring: formData.isRecurring,
        recurrenceFrequency: formData.isRecurring ? 'weekly' : null,
        recurrenceEndDate: formData.isRecurring ? recurrenceEndDate : null,
        recurrenceDays: formData.isRecurring ? (formData.recurrenceDays || [1, 2, 3, 4, 5]) : null,
        createdBy: currentUser.displayName || currentUser.email,
        createdById: currentUser.uid,
        status: 'scheduled',
        platform: 'google-meet',
      };

      if (selectedMeeting) {
        await updateDoc(doc(db, MEETINGS_COLLECTION, selectedMeeting.id), {
          ...meetingData, updatedAt: serverTimestamp()
        });
        setSuccess('Meeting updated successfully!');
      } else {
        await addDoc(collection(db, MEETINGS_COLLECTION), {
          ...meetingData, createdAt: serverTimestamp()
        });
        setSuccess('Meeting scheduled successfully!');
      }

      setShowModal(false);
      setSelectedMeeting(null);
      resetForm();
      loadMeetings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save meeting: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (meeting) => {
    const base = meeting.baseMeeting || meeting;
    const date = new Date(base.scheduledDateTime);
    setFormData({
      title: base.title,
      description: base.description || '',
      meetLink: base.meetLink || '',
      scheduledDate: date.toISOString().split('T')[0],
      scheduledTime: date.toTimeString().slice(0, 5),
      duration: base.duration?.toString() || '60',
      programId: base.programId || '',
      cohortId: base.cohortId || '',
      cohortIds: Array.isArray(base.cohortIds) ? base.cohortIds : (base.cohortId ? [base.cohortId] : []),
      sendReminder: base.sendReminder !== false,
      reminderMinutes: base.reminderMinutes || 30,
      assignedUserIds: base.assignedUserIds || [],
      assignedUserEmails: Array.isArray(base.assignedUserEmails) ? base.assignedUserEmails.join('\n') : '',
      isRecurring: base.isRecurring || false,
      recurrenceDays: Array.isArray(base.recurrenceDays) ? base.recurrenceDays : [1, 2, 3, 4, 5],
      recurrenceEndDate: base.recurrenceEndDate?.toDate
        ? base.recurrenceEndDate.toDate().toISOString().split('T')[0]
        : (base.recurrenceEndDate ? new Date(base.recurrenceEndDate).toISOString().split('T')[0] : ''),
    });
    setSelectedMeeting(base);
    setShowModal(true);
  };

  const handleDelete = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, MEETINGS_COLLECTION, meetingId));
      setSuccess('Meeting deleted.');
      loadMeetings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete meeting: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', meetLink: '',
      scheduledDate: '', scheduledTime: '', duration: '60',
      programId: '', cohortId: '', cohortIds: [], assignedUserIds: [],
      assignedUserEmails: '', sendReminder: true, reminderMinutes: 30,
      isRecurring: false, recurrenceEndDate: '', recurrenceDays: [1, 2, 3, 4, 5],
    });
  };

  const toggleAssignedUser = (userId) => {
    setFormData(prev => {
      const current = prev.assignedUserIds || [];
      return {
        ...prev,
        assignedUserIds: current.includes(userId)
          ? current.filter(id => id !== userId)
          : [...current, userId],
      };
    });
  };

  const formatDateTime = (date) =>
    new Date(date).toLocaleString('en-ZA', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const getTimeUntilMeeting = (dt) => {
    const diffMs = new Date(dt) - new Date();
    if (diffMs < 0) return 'Meeting has started';
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 60) return `Starts in ${diffMins} minutes`;
    if (diffHours < 24) return `Starts in ${diffHours} hours`;
    return `Starts in ${diffDays} days`;
  };

  const getMeetingStatus = (dt, duration) => {
    const now = new Date();
    const start = new Date(dt);
    const end = new Date(start.getTime() + duration * 60000);
    if (now < start) return 'upcoming';
    if (now >= start && now < end) return 'live';
    return 'completed';
  };

  const getProgramName = (id) => {
    if (!id) return 'All Programs';
    const p = programs.find(p => p.id === id);
    return p?.name || p?.programName || 'Unknown Program';
  };

  const getCohortName = (id) => {
    if (!id) return 'All Cohorts';
    if (userProfile?.role === 'learner' && userProfile?.cohortId === id)
      return userProfile?.cohortName || 'Your Cohort';
    const c = cohorts.find(c => c.id === id);
    return c?.name || c?.cohortName || 'Unknown Cohort';
  };

  const filteredMeetings = meetings.filter(m => {
    const s = getMeetingStatus(m.scheduledDateTime, m.duration);
    return s === 'upcoming' || s === 'live';
  });

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0">📹 Upcoming Google Meet Sessions</h6>
          </div>
          {allowScheduleEdits && (
            <Button variant="light" size="sm" onClick={() => { resetForm(); setSelectedMeeting(null); setShowModal(true); }}>
              ➕ Add Meeting
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading meetings...</p>
            </div>
          ) : filteredMeetings.length === 0 ? (
            <Alert variant="info">
              <strong>No upcoming meetings scheduled</strong>
              <p className="mb-0 mt-2">
                {allowScheduleEdits
                  ? 'Click "Add Meeting" to schedule a new Google Meet session.'
                  : 'Check back later for scheduled sessions.'}
              </p>
            </Alert>
          ) : (
            <ListGroup variant="flush">
              {filteredMeetings.map(meeting => {
                const status = getMeetingStatus(meeting.scheduledDateTime, meeting.duration);
                const isRecurringMeeting = meeting.isRecurring || meeting.baseMeeting?.isRecurring;
                return (
                  <ListGroup.Item key={meeting.id}>
                    <Row className="align-items-center">
                      <Col md={8}>
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            {status === 'live'
                              ? <Badge bg="danger" className="pulse-animation">● LIVE NOW</Badge>
                              : <Badge bg="success">Upcoming</Badge>}
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{meeting.title}</h6>
                            {meeting.description && <p className="text-muted small mb-1">{meeting.description}</p>}
                            <div className="small text-muted">
                              📅 {formatDateTime(meeting.scheduledDateTime)}<br />
                              ⏱️ {meeting.duration} minutes<br />
                              ⏰ {getTimeUntilMeeting(meeting.scheduledDateTime)}
                            </div>
                            {meeting.programId && <Badge bg="primary" className="mt-1 me-1">{getProgramName(meeting.programId)}</Badge>}
                            {meeting.cohortId && <Badge bg="info" className="mt-1">{getCohortName(meeting.cohortId)}</Badge>}
                            {isRecurringMeeting && <Badge bg="secondary" className="mt-1 ms-1">Recurring weekdays</Badge>}
                          </div>
                        </div>
                      </Col>
                      <Col md={4} className="text-end">
                        <Button
                          variant={status === 'live' ? 'danger' : 'primary'}
                          size="sm"
                          href={meeting.meetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mb-2 w-100"
                        >
                          {status === 'live' ? '🔴 Join Now' : '📹 Join Meeting'}
                        </Button>
                        {isFacilitatorOrAdmin && (
                          <div className="d-flex gap-1">
                            <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(meeting)} className="flex-grow-1">Edit</Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(meeting.baseMeetingId || meeting.id)} className="flex-grow-1">Delete</Button>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={() => { setShowModal(false); setSelectedMeeting(null); resetForm(); }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMeeting ? '✏️ Edit Meeting' : '📹 Schedule Google Meet Session'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Meeting Title *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Week 2 Catch-up Session"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea" rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of what will be covered"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control type="date" value={formData.scheduledDate}
                    onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Time *</Form.Label>
                  <Form.Control type="time" value={formData.scheduledTime}
                    onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration (minutes)</Form.Label>
                  <Form.Select value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                    <option value="180">3 hours</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Google Meet Link *</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="url"
                  value={formData.meetLink}
                  onChange={(e) => setFormData({ ...formData, meetLink: e.target.value })}
                  placeholder="https://meet.google.com/abc-defg-hij"
                />
                {isGoogleAuthenticated && (
                  <Button variant="outline-success" onClick={handleCreateWithGoogle} disabled={creatingMeeting} style={{ whiteSpace: 'nowrap' }}>
                    {creatingMeeting ? <Spinner size="sm" /> : '🗓️ Create via Calendar'}
                  </Button>
                )}
              </div>
              <Form.Text className="text-muted">
                {isGoogleAuthenticated
                  ? 'Paste an existing link or click "Create via Calendar" to generate one automatically.'
                  : 'Paste the Google Meet link from your calendar invite. Connect Google above to auto-create.'}
              </Form.Text>
            </Form.Group>

            {!isGoogleAuthenticated && allowScheduleEdits && (
              <Alert variant="info" className="mb-3">
                <Alert.Link onClick={signInWithGoogle} style={{ cursor: 'pointer' }}>Connect your Google account</Alert.Link>
                {' '}to auto-generate Meet links from within the LMS.
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox" id="recurringMeeting" label="Recurring schedule"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
              />
            </Form.Group>

            {formData.isRecurring && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Recurring Days</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    {[0, 1, 2, 3, 4, 5, 6].map(day => {
                      const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const selected = (formData.recurrenceDays || []).includes(day);
                      return (
                        <Form.Check
                          key={`day-${day}`} type="checkbox" id={`day-${day}`}
                          label={labels[day]} checked={selected}
                          onChange={() => {
                            const next = selected
                              ? formData.recurrenceDays.filter(d => d !== day)
                              : [...formData.recurrenceDays, day];
                            setFormData({ ...formData, recurrenceDays: next });
                          }}
                        />
                      );
                    })}
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Recurrence end date (optional)</Form.Label>
                  <Form.Control type="date" value={formData.recurrenceEndDate}
                    onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })} />
                </Form.Group>
              </>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Program (Optional)</Form.Label>
                  <Form.Select value={formData.programId}
                    onChange={(e) => setFormData({ ...formData, programId: e.target.value, cohortId: '', cohortIds: [] })}>
                    <option value="">All Programs</option>
                    {programs.map(p => <option key={p.id} value={p.id}>{p.name || p.programName}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cohort (Optional)</Form.Label>
                  <Form.Select value={formData.cohortId}
                    onChange={(e) => {
                      const val = e.target.value;
                      const nextIds = val ? Array.from(new Set([val, ...(formData.cohortIds || [])])) : formData.cohortIds;
                      setFormData({ ...formData, cohortId: val, cohortIds: nextIds });
                    }}>
                    <option value="">All Cohorts</option>
                    {(formData.programId ? cohorts.filter(c => c.programId === formData.programId) : cohorts)
                      .map(c => <option key={c.id} value={c.id}>{c.name || c.cohortName}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Additional Cohorts</Form.Label>
              <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                {(formData.programId ? cohorts.filter(c => c.programId === formData.programId) : cohorts)
                  .map(cohort => (
                    <Form.Check
                      key={`cohort-${cohort.id}`} type="checkbox" id={`cohort-${cohort.id}`}
                      label={cohort.name || cohort.cohortName}
                      checked={(formData.cohortIds || []).includes(cohort.id)}
                      onChange={() => {
                        const current = formData.cohortIds || [];
                        setFormData({
                          ...formData,
                          cohortIds: current.includes(cohort.id)
                            ? current.filter(id => id !== cohort.id)
                            : [...current, cohort.id],
                        });
                      }}
                    />
                  ))}
              </div>
            </Form.Group>

            {isFacilitatorOrAdmin && (
              <>
                <hr />
                <h6 className="mb-3">Visible To (Optional)</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Specific Emails</Form.Label>
                  <Form.Control
                    as="textarea" rows={3}
                    value={formData.assignedUserEmails}
                    onChange={(e) => setFormData({ ...formData, assignedUserEmails: e.target.value })}
                    placeholder="person1@example.com&#10;person2@example.com"
                  />
                  <Form.Text className="text-muted">One email per line. These users can see the meeting even if not in the assigned cohort.</Form.Text>
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Label>Facilitators</Form.Label>
                    <div className="border rounded p-2 mb-3" style={{ maxHeight: 160, overflowY: 'auto' }}>
                      {facilitators.length === 0
                        ? <div className="text-muted small">No facilitators found.</div>
                        : facilitators.map(u => (
                          <Form.Check key={u.id} type="checkbox" id={`fac-${u.id}`}
                            label={u.displayName || u.email}
                            checked={(formData.assignedUserIds || []).includes(u.id)}
                            onChange={() => toggleAssignedUser(u.id)} />
                        ))}
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>External Users</Form.Label>
                    <div className="border rounded p-2 mb-3" style={{ maxHeight: 160, overflowY: 'auto' }}>
                      {externalUsers.length === 0
                        ? <div className="text-muted small">No external users found.</div>
                        : externalUsers.map(u => (
                          <Form.Check key={u.id} type="checkbox" id={`ext-${u.id}`}
                            label={u.displayName || u.email}
                            checked={(formData.assignedUserIds || []).includes(u.id)}
                            onChange={() => toggleAssignedUser(u.id)} />
                        ))}
                    </div>
                  </Col>
                </Row>
              </>
            )}

            <hr />
            <h6 className="mb-3">Reminder Settings</h6>
            <Form.Group className="mb-3">
              <Form.Check type="checkbox" id="sendReminder" label="Send reminder to learners"
                checked={formData.sendReminder}
                onChange={(e) => setFormData({ ...formData, sendReminder: e.target.checked })} />
            </Form.Group>
            {formData.sendReminder && (
              <Form.Group className="mb-3">
                <Form.Label>Send reminder</Form.Label>
                <Form.Select value={formData.reminderMinutes}
                  onChange={(e) => setFormData({ ...formData, reminderMinutes: e.target.value })}>
                  <option value="15">15 minutes before</option>
                  <option value="30">30 minutes before</option>
                  <option value="60">1 hour before</option>
                  <option value="120">2 hours before</option>
                  <option value="1440">1 day before</option>
                </Form.Select>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); setSelectedMeeting(null); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading && <Spinner animation="border" size="sm" className="me-2" />}
              {selectedMeeting ? 'Update Meeting' : 'Schedule Meeting'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .pulse-animation { animation: pulse 1.5s ease-in-out infinite; }
      `}</style>
    </>
  );
};

export default GoogleMeetLinks;
